import type { CSSObject } from '@mui/material/styles';
import { MarkElementProps } from '@mui/x-charts/LineChart';
import { BiometricDatapointRecord } from 'services/biometricsService';

export type MealReading =
  | 'preBreakfast'
  | 'postBreakfast'
  | 'preLunch'
  | 'postLunch'
  | 'preDinner'
  | 'postDinner'
  | 'bedtime';

export const MEAL_READINGS: MealReading[] = [
  'preBreakfast',
  'postBreakfast',
  'preLunch',
  'postLunch',
  'preDinner',
  'postDinner',
  'bedtime',
];

// Figma (WFMOB-16150): fixed pre-meal colors and shapes; post-meals reuse the shape, hollow.
// Post-meal hues are approximations.
export const MEAL_COLORS: Record<MealReading, string> = {
  preBreakfast: '#009FB8',
  postBreakfast: '#7B5EA7',
  preLunch: '#57A503',
  postLunch: '#005A89',
  preDinner: '#E34964',
  postDinner: '#FF961B',
  bedtime: '#C6840C',
};

export const MEAL_SHAPES: Record<MealReading, MarkElementProps['shape']> = {
  preBreakfast: 'square',
  postBreakfast: 'square',
  preLunch: 'circle',
  postLunch: 'circle',
  preDinner: 'triangle',
  postDinner: 'triangle',
  bedtime: 'diamond',
};

export const isHollowMeal = (meal: MealReading): boolean => meal.startsWith('post');

export const MEAL_LABEL_KEYS: Record<MealReading, string> = {
  preBreakfast: 'filter_label_prebreakfast',
  postBreakfast: 'filter_label_postbreakfast',
  preLunch: 'filter_label_prelunch',
  postLunch: 'filter_label_postlunch',
  preDinner: 'filter_label_predinner',
  postDinner: 'filter_label_postdinner',
  bedtime: 'filter_label_bedtime',
};

const MEAL_SHAPE_CLIP_PATHS: Partial<Record<MarkElementProps['shape'], string>> = {
  triangle: 'polygon(50% 0%, 0% 100%, 100% 100%)',
  diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
};

const SWATCH_OUTLINE = 1.5;

// A hollow triangle's outline is even only if the inner triangle is scaled toward its incenter.
const hollowTriangleClipPath = (size: number): string => {
  const inradius = size / (1 + 2 * Math.sqrt(1.25));
  const centerX = size / 2;
  const centerY = size - inradius;
  const scale = 1 - SWATCH_OUTLINE / inradius;
  const point = (x: number, y: number) =>
    `${(centerX + scale * (x - centerX)).toFixed(2)}px ${(centerY + scale * (y - centerY)).toFixed(2)}px`;
  return `polygon(${point(size / 2, 0)}, ${point(0, size)}, ${point(size, size)})`;
};

export const getMealSwatchStyles = (meal: MealReading, size: number): CSSObject => {
  const shape = MEAL_SHAPES[meal];
  const shapeStyles: CSSObject = {
    borderRadius: shape === 'circle' ? '50%' : undefined,
    clipPath: MEAL_SHAPE_CLIP_PATHS[shape],
  };

  return {
    position: 'relative',
    width: size,
    height: size,
    flexShrink: 0,
    backgroundColor: MEAL_COLORS[meal],
    ...shapeStyles,
    ...(isHollowMeal(meal) && {
      '&::after': {
        content: '""',
        position: 'absolute',
        backgroundColor: '#fff',
        ...shapeStyles,
        ...(shape === 'triangle'
          ? { inset: 0, clipPath: hollowTriangleClipPath(size) }
          : { inset: SWATCH_OUTLINE }),
      },
    }),
  };
};

// The live API's terms for the fasting-breakfast and post-meal readings.
const MEAL_RELATIONSHIP_MAP: Record<string, MealReading> = {
  prebreakfast: 'preBreakfast',
  fastingbreakfast: 'preBreakfast',
  postbreakfast: 'postBreakfast',
  twohourspostbreakfast: 'postBreakfast',
  prelunch: 'preLunch',
  postlunch: 'postLunch',
  twohourspostlunch: 'postLunch',
  predinner: 'preDinner',
  postdinner: 'postDinner',
  twohourspostdinner: 'postDinner',
  bedtime: 'bedtime',
};

export const normalizeMealRelationship = (value: string | null): MealReading | null => {
  if (!value) return null;
  const key = value.toLowerCase().replace(/[^a-z]/g, '');
  return MEAL_RELATIONSHIP_MAP[key] ?? null;
};

export interface BloodGlucoseWeekSeries {
  days: string[];
  byMeal: Record<MealReading, Array<number | null>>;
  hasReadings: boolean;
}

export const toBloodGlucoseWeekSeries = (
  datapoints: BiometricDatapointRecord[],
  days: string[],
): BloodGlucoseWeekSeries => {
  const byMeal = MEAL_READINGS.reduce(
    (acc, meal) => {
      acc[meal] = days.map(() => null);
      return acc;
    },
    {} as Record<MealReading, Array<number | null>>,
  );

  let hasReadings = false;
  datapoints.forEach((point) => {
    const meal = normalizeMealRelationship(point.relationshipToMeal);
    if (!meal) return;
    const dayIndex = days.indexOf(point.userLocalDateCaptured.slice(0, 10));
    if (dayIndex === -1) return;
    byMeal[meal][dayIndex] = point.value;
    hasReadings = true;
  });

  return { days, byMeal, hasReadings };
};

export const mealsWithData = (series: BloodGlucoseWeekSeries): MealReading[] =>
  MEAL_READINGS.filter((meal) => series.byMeal[meal].some((value) => value !== null));
